import React, { useEffect, useRef, useState } from "react";
import ContactForm from "../components/ContactForm";

const QUICK_CARDS = [
  {
    icon: "📧",
    title: "Email Secretariat",
    value: "info@dingonet.org",
    description:
      "For general inquiries, membership, and partnership discussions. We respond within 2 business days.",
    cta: "Send Email →",
  },
  {
    icon: "📞",
    title: "Contact",
    value:
      "Laurent Wambura, Executive Director\n0757724717\n\nSuhaila Stanthon, Lead of Advocacy and Communication\n0744388179",
    description:
      "Reach the Executive Director or Lead of Advocacy and Communication for network matters. For urgent disaster coordination, call directly.",
    cta: "Call Now →",
  },
  {
    icon: "📍",
    title: "Secretariat Office",
    value: "Dar es Salaam, Tanzania",
    description:
      "DINGONET Secretariat is hosted by RAPID-Tanzania. P.O. Box 11189, Dar es Salaam. Visits by appointment only — please email ahead.",
    cta: "Get Directions →",
  },
];

const INQUIRY_BADGE_STYLES = {
  "General Inquiry": "bg-blue-500/10 text-blue-400 border-blue-500/20",
  Membership: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
  Partnership: "bg-purple-500/10 text-purple-400 border-purple-500/20",
  "Emergency/Response": "bg-red-500/10 text-red-400 border-red-500/20",
  Media: "bg-amber-500/10 text-amber-400 border-amber-500/20",
  Other: "bg-slate-500/10 text-slate-400 border-slate-500/20",
};

const INQUIRY_GUIDE_TILES = [
  {
    badge: "General Inquiry",
    response: "~2 days",
    icon: "💬",
    title: "General Questions",
    description:
      "Learn more about DINGONET, our mission, strategic areas of focus, or how we coordinate disaster response in Tanzania.",
  },
  {
    badge: "Membership",
    response: "~5 days",
    icon: "🤝",
    title: "Membership Applications",
    description:
      "Apply to join DINGONET. Membership is open to NGOs with active engagement in disaster management or humanitarian action in Tanzania.",
  },
  {
    badge: "Partnership",
    response: "~3 days",
    icon: "🌐",
    title: "Partnerships & Funding",
    description:
      "Explore strategic partnerships, consortium programming, joint funding proposals, or donor engagement with the DINGONET network.",
  },
  {
    badge: "Emergency/Response",
    response: "Immediate",
    icon: "🚨",
    title: "Disaster Coordination",
    description:
      "Report an active disaster or request emergency NGO coordination support. Call the Executive Director (0757724717) or Lead of Advocacy and Communication (0744388179) for fastest response.",
  },
  {
    badge: "Media",
    response: "~1 day",
    icon: "📰",
    title: "Media & Press",
    description:
      "Press inquiries, interview requests, official statements, or information about DINGONET's humanitarian coordination activities and achievements.",
  },
  {
    badge: "Other",
    response: "~2 days",
    icon: "📋",
    title: "Other Inquiries",
    description:
      "Can't find your category? Send us a message and our Secretariat team will route it to the right person within the network.",
  },
];

const SOCIAL_TILES = [
  { icon: "📧", label: "Email Updates", sub: "Subscribe to newsletter" },
  { icon: "💬", label: "WhatsApp Group", sub: "Join coordination group" },
  { icon: "🔗", label: "LinkedIn", sub: "Follow DINGONET updates" },
  { icon: "🌐", label: "RAPID-Tanzania", sub: "Visit secretariat website" },
];

const STATS = [
  { target: "20+", label: "Member Organizations", sub: "Ready to coordinate" },
  { target: "24/7", label: "Emergency Coordination", sub: "Always available" },
  { target: "2 days", label: "Average Response Time", sub: "For general inquiries" },
];

const useInView = (threshold = 0.15) => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    if (!ref.current || inView) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
        }
      },
      { threshold }
    );

    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold, inView]);

  return [ref, inView];
};

const useCounter = (target, duration = 1800, shouldStart) => {
  const [value, setValue] = useState(typeof target === "number" ? 0 : target);

  useEffect(() => {
    if (!shouldStart) {
      return undefined;
    }
    if (typeof target !== "number") {
      setValue(target);
      return undefined;
    }

    let animationFrameId;
    let startTime;

    const tick = (timestamp) => {
      if (!startTime) {
        startTime = timestamp;
      }
      const elapsed = timestamp - startTime;
      const progress = Math.min(elapsed / duration, 1);
      setValue(Math.floor(progress * target));

      if (progress < 1) {
        animationFrameId = window.requestAnimationFrame(tick);
      } else {
        setValue(target);
      }
    };

    animationFrameId = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(animationFrameId);
  }, [duration, shouldStart, target]);

  return value;
};

const ContactPage = () => {
  const [quickCardsRef, quickCardsInView] = useInView(0.2);
  const [formRef, formInView] = useInView(0.15);
  const [guideRef, guideInView] = useInView(0.15);
  const [statsRef, statsInView] = useInView(0.3);
  const [socialRef, socialInView] = useInView(0.15);

  const statsValues = [
    useCounter(20, 1800, statsInView),
    useCounter(24, 1800, statsInView),
    useCounter(2, 1800, statsInView),
  ];

  return (
    <div className="min-h-screen bg-[#0B1629] text-white">
      <section id="get-in-touch" className="relative scroll-mt-28 overflow-hidden pb-20 pt-36">
        <div className="pointer-events-none">
          <div className="absolute left-1/2 top-1/2 h-[380px] w-[750px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#00E5CC]/5 blur-3xl" />
          <div className="absolute right-16 top-20 h-60 w-60 rounded-full bg-teal-400/5 blur-2xl" />
          <div className="absolute bottom-10 left-10 h-48 w-48 rounded-full bg-[#00E5CC]/3 blur-2xl" />
        </div>
        <div className="relative mx-auto max-w-4xl px-6 text-center">
          <span className="mb-6 inline-block rounded-full border border-[#00E5CC]/30 px-4 py-1.5 text-xs uppercase tracking-[0.25em] text-[#00E5CC]">
            Get In Touch
          </span>
          <h1 className="mb-6 text-5xl font-black leading-tight md:text-7xl">
            Let&apos;s Build Resilience <span className="text-[#00E5CC]">Together</span>
          </h1>
          <p className="mx-auto max-w-2xl text-lg leading-relaxed text-slate-400">
            Whether you&apos;re looking to join the network, explore a partnership, report a disaster, or simply
            learn more about DINGONET — we&apos;re here and ready to respond.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-6 pb-16" ref={quickCardsRef}>
        <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
          {QUICK_CARDS.map((card, index) => (
            <div
              key={card.title}
              className={`rounded-2xl border border-white/10 bg-gradient-to-br from-slate-800/80 to-slate-900/80 p-8 text-center transition-all duration-500 hover:border-[#00E5CC]/40 hover:shadow-[0_0_40px_rgba(0,229,204,0.12)] ${
                quickCardsInView ? "translate-y-0 opacity-100" : "translate-y-5 opacity-0"
              }`}
              style={{ transitionDelay: `${index * 100}ms` }}
            >
              <div className="mb-4 text-4xl">{card.icon}</div>
              <h3 className="mb-2 text-lg font-bold text-white">{card.title}</h3>
              <p className="whitespace-pre-line break-words text-sm font-semibold text-[#00E5CC]">{card.value}</p>
              <p className="mt-3 text-xs leading-relaxed text-slate-400">{card.description}</p>
              <button type="button" className="mt-4 inline-block cursor-pointer text-xs font-semibold text-[#00E5CC] hover:underline">
                {card.cta}
              </button>
            </div>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-6 pb-28" ref={formRef}>
        <div className="grid grid-cols-1 gap-10 md:grid-cols-3">
          <div
            className={`md:col-span-2 rounded-3xl border border-white/10 bg-gradient-to-br from-slate-800/80 to-slate-900/80 p-8 transition-all duration-700 md:p-10 ${
              formInView ? "translate-y-0 opacity-100" : "translate-y-5 opacity-0"
            }`}
          >
            <ContactForm />
          </div>

          <div className="flex flex-col gap-6 md:col-span-1">
            <div className="rounded-2xl border border-white/10 bg-white/5 p-6">
              <h3 className="mb-4 text-base font-bold text-white">🏢 Secretariat</h3>
              <div className="flex flex-col gap-3">
                <div className="flex items-start gap-3">
                  <span className="text-sm text-[#00E5CC]">📍</span>
                  <div>
                    <p className="text-xs uppercase tracking-wider text-slate-500">Address</p>
                    <p className="mt-0.5 text-sm text-slate-300">P.O. Box 11189, Dar es Salaam, Tanzania</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-sm text-[#00E5CC]">📧</span>
                  <div>
                    <p className="text-xs uppercase tracking-wider text-slate-500">Email</p>
                    <button type="button" className="mt-0.5 cursor-pointer text-sm text-[#00E5CC] hover:underline">
                      info@dingonet.org
                    </button>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-sm text-[#00E5CC]">📞</span>
                  <div>
                    <p className="text-xs uppercase tracking-wider text-slate-500">Contact</p>
                    <div className="mt-2 space-y-3 text-sm text-slate-300">
                      <div>
                        <p className="font-medium text-white">Laurent Wambura, Executive Director</p>
                        <a href="tel:+255757724717" className="mt-0.5 block text-[#00E5CC] hover:underline">
                          0757724717
                        </a>
                      </div>
                      <div>
                        <p className="font-medium text-white">Suhaila Stanthon, Lead of Advocacy and Communication</p>
                        <a href="tel:+255744388179" className="mt-0.5 block text-[#00E5CC] hover:underline">
                          0744388179
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-sm text-[#00E5CC]">🌐</span>
                  <div>
                    <p className="text-xs uppercase tracking-wider text-slate-500">Hosted By</p>
                    <p className="mt-0.5 text-sm text-slate-300">RAPID-Tanzania</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-sm text-[#00E5CC]">📋</span>
                  <div>
                    <p className="text-xs uppercase tracking-wider text-slate-500">Reg. Number</p>
                    <p className="mt-0.5 text-sm text-slate-300">8894 (Jan 2026)</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-white/10 bg-white/5 p-6">
              <h3 className="mb-4 text-base font-bold text-white">🕐 Office Hours</h3>
              {[
                ["Monday – Friday", "8:00 AM – 5:00 PM EAT", "text-[#00E5CC] font-medium"],
                ["Saturday", "By Appointment", "text-slate-400"],
                ["Sunday", "Closed", "text-slate-500"],
                ["Emergency Line", "24/7 Coordination", "text-red-400 font-medium"],
              ].map(([day, value, valueClass], index) => (
                <div
                  key={day}
                  className={`flex items-center justify-between py-2 text-sm ${
                    index === 3 ? "" : "border-b border-white/5"
                  }`}
                >
                  <span className="text-slate-300">{day}</span>
                  <span className={valueClass}>{value}</span>
                </div>
              ))}
            </div>

            <div className="rounded-2xl border border-white/10 bg-white/5 p-6">
              <h3 className="mb-4 text-base font-bold text-white">⚡ Response Times</h3>
              <div className="flex flex-col gap-3">
                {[
                  ["bg-emerald-400", "General Inquiries: 2 business days"],
                  ["bg-amber-400", "Membership Applications: 5 business days"],
                  ["bg-blue-400", "Partnership Proposals: 3 business days"],
                  ["bg-red-400", "Emergency Coordination: Immediate"],
                  ["bg-purple-400", "Media Requests: 1 business day"],
                ].map(([dotColor, text]) => (
                  <div className="flex items-center gap-3" key={text}>
                    <span className={`h-2 w-2 flex-shrink-0 rounded-full ${dotColor}`} />
                    <p className="text-xs leading-relaxed text-slate-300">{text}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-2xl border border-red-500/20 bg-red-500/8 p-6">
              <div className="flex items-start gap-3">
                <span className="text-2xl">🚨</span>
                <div>
                  <h3 className="mb-2 text-sm font-bold text-red-400">Disaster Coordination</h3>
                  <p className="text-xs leading-relaxed text-slate-400">
                    For active disaster situations requiring immediate NGO coordination, call the Executive Director on{" "}
                    <a href="tel:+255757724717" className="text-[#00E5CC] hover:underline">
                      0757724717
                    </a>{" "}
                    or the Lead of Advocacy and Communication on{" "}
                    <a href="tel:+255744388179" className="text-[#00E5CC] hover:underline">
                      0744388179
                    </a>
                    . DINGONET operates 24/7 emergency coordination support.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-6 pb-28">
        <div className="mb-10 text-center">
          <h2 className="text-3xl font-black text-white">
            Find <span className="text-[#00E5CC]">Our Office</span>
          </h2>
          <p className="mx-auto mt-4 max-w-md text-slate-400">
            The DINGONET Secretariat is hosted by RAPID-Tanzania in Dar es Salaam — Tanzania&apos;s humanitarian
            coordination hub.
          </p>
        </div>
        <div className="relative h-80 w-full overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-slate-800 to-slate-900">
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d4499.088798875718!2d39.2259838!3d-6.7086383000000005!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x185c51005f60fdd5%3A0x5922d2fabb337d87!2sDingonet!5e1!3m2!1sen!2stz!4v1781743884047!5m2!1sen!2stz"
            width="100%"
            height="100%"
            style={{ border: 0, filter: "invert(90%) hue-rotate(180deg) saturate(0.8)" }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="DINGONET Office Location"
            className="pointer-events-auto"
          />
          <div className="pointer-events-none absolute inset-0" />
          <div className="absolute bottom-4 left-4 flex items-center gap-3 rounded-xl border border-white/10 bg-[#0B1629]/90 px-5 py-4 backdrop-blur-md">
            <span className="text-xl text-[#00E5CC]">📍</span>
            <div>
              <p className="text-sm font-bold text-white">DINGONET Secretariat</p>
              <p className="mt-0.5 text-xs text-slate-400">Hosted by RAPID-Tanzania · Dar es Salaam</p>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-6 pb-28" ref={guideRef}>
        <div className="mb-12 text-center">
          <h2 className="text-3xl font-black text-white">
            What Would You <span className="text-[#00E5CC]">Like to Discuss?</span>
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-slate-400">
            Choose the right channel based on your inquiry type — so we connect you with the right team immediately.
          </p>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {INQUIRY_GUIDE_TILES.map((tile, index) => (
            <div
              key={tile.title}
              className={`rounded-2xl border border-white/8 bg-white/5 p-6 transition-all duration-300 hover:border-[#00E5CC]/30 hover:bg-[#00E5CC]/3 ${
                guideInView ? "translate-y-0 opacity-100" : "translate-y-5 opacity-0"
              }`}
              style={{ transitionDelay: `${index * 80}ms` }}
            >
              <div className="flex items-center justify-between gap-3">
                <span className={`rounded-full border px-2.5 py-1 text-[10px] font-medium ${INQUIRY_BADGE_STYLES[tile.badge]}`}>
                  {tile.badge}
                </span>
                <span className="rounded-full border border-white/8 px-2 py-0.5 text-[10px] text-slate-500">{tile.response}</span>
              </div>
              <div className="mb-3 mt-4 text-3xl">{tile.icon}</div>
              <h3 className="mb-2 text-sm font-bold text-white">{tile.title}</h3>
              <p className="text-xs leading-relaxed text-slate-400">{tile.description}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-6 pb-24">
        <div className="grid grid-cols-1 items-center gap-8 rounded-3xl border border-[#00E5CC]/15 bg-gradient-to-r from-[#00E5CC]/8 via-[#00BFA5]/5 to-transparent p-10 md:grid-cols-2">
          <div>
            <span className="mb-4 inline-block rounded-full border border-[#00E5CC]/30 bg-[#00E5CC]/10 px-4 py-1.5 text-xs uppercase tracking-[0.22em] text-[#00E5CC]">
              Why Contact Us
            </span>
            <h2 className="text-2xl font-black text-white md:text-3xl">
              We Coordinate So <span className="text-[#00E5CC]">Communities Survive</span>
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-slate-400">
              Every message, every partnership, every membership inquiry strengthens DINGONET&apos;s ability to
              coordinate faster, reach further, and save more lives across Tanzania&apos;s disaster-prone regions.
            </p>
          </div>
          <div className="flex flex-col gap-4" ref={statsRef}>
            {STATS.map((stat, index) => (
              <div
                key={stat.label}
                className={`flex items-center gap-4 transition-all duration-700 ${
                  statsInView ? "translate-y-0 opacity-100" : "translate-y-5 opacity-0"
                }`}
                style={{ transitionDelay: `${index * 150}ms` }}
              >
                <span className="text-3xl font-black text-[#00E5CC]">
                  {index === 0 && `${statsValues[0]}+`}
                  {index === 1 && `${statsValues[1]}/7`}
                  {index === 2 && `${statsValues[2]} days`}
                </span>
                <div>
                  <p className="text-sm font-semibold text-white">{stat.label}</p>
                  <p className="text-xs text-slate-400">{stat.sub}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-6 pb-24 text-center" ref={socialRef}>
        <h2 className="mb-3 text-2xl font-black text-white">
          Connect With <span className="text-[#00E5CC]">DINGONET</span>
        </h2>
        <p className="mb-8 text-sm text-slate-400">
          Follow our coordination updates, disaster alerts, and humanitarian news across our channels.
        </p>
        <div className="flex flex-wrap justify-center gap-4">
          {SOCIAL_TILES.map((tile, index) => (
            <button
              key={tile.label}
              type="button"
              className={`cursor-pointer rounded-2xl border border-white/10 bg-white/5 px-8 py-5 transition-all duration-300 hover:border-[#00E5CC]/30 hover:bg-[#00E5CC]/5 ${
                socialInView ? "translate-y-0 opacity-100" : "translate-y-5 opacity-0"
              }`}
              style={{ transitionDelay: `${index * 80}ms` }}
            >
              <div className="mb-2 text-2xl">{tile.icon}</div>
              <p className="text-sm font-semibold text-white">{tile.label}</p>
              <p className="mt-1 text-xs text-slate-400">{tile.sub}</p>
            </button>
          ))}
        </div>
      </section>

      <footer className="border-t border-white/5 py-8 text-center text-sm text-slate-500">
        <p>
          DINGONET Secretariat (Hosted by RAPID-Tanzania) · P.O. Box 11189, Dar es Salaam · info@dingonet.org ·
          Executive Director 0757724717 · Lead of Advocacy and Communication 0744388179
        </p>
        <p className="mt-2">Reg. No. 8894 · Registered January 2026</p>
      </footer>
    </div>
  );
};

export default ContactPage;
